import ComponentElement from 'components/component-element';
import Review from './review';
import StarsInput from './stars-input';
import PhotogalleryUploader from './photogallery-uploader';
import {createElement, autoUpdateTextareaHeight} from 'methods/utils';
import Scroll from 'components/scroll';

const getStorage = (key, defaultValue = null) => {
    const value = sessionStorage.getItem(`${location.pathname}:review-create:${key}`);
    return value === null ? defaultValue : value;
}

const setStorage = (key, value) => {
    return sessionStorage.setItem(`${location.pathname}:review-create:${key}`, value);
}

const removeStorage = (key) => {
    return sessionStorage.removeItem(`${location.pathname}:review-create:${key}`);
}

/**
 * Класс обработчик формы создания отзыва
 * @param {Element} $element Блок с формой
 */
function ReviewCreate(reviews, $element, $reviewsListContainer, options = {}) {
    ComponentElement.call(this, $element, options);

    this.reviews = reviews;
    this.$reviewsListContainer = $reviewsListContainer;

    this.$open = this.$element.querySelector('.js-reviewCreateOpen');
    this.$open.addEventListener('click', e => {
        this.reviews.auth().then(() => {
            this.toggle();
        });
    });

    this.$submit = this.$element.querySelector('.js-submit');
    this.$form   = this.$element.querySelector('.js-form');
    this.$error  = this.$element.querySelector('.js-reviewCreateError');

    this.$form.addEventListener('input', this.onFormInputEvent.bind(this));
    this.$form.addEventListener('change', this.onFormChangeEvent.bind(this));
    this.$form.addEventListener('submit', this.onFormSubmitEvent.bind(this));

    this.starsInput = new StarsInput(this.$element.querySelector('.js-starsInput'), {
        value: getStorage('stars', 5),
        maxValue: 5,
        minValue: 1,
    });

    this.starsInput.on('change', value => {
        setStorage('stars', value);
        this.update();
    });

    this.photogalleryUploader = new PhotogalleryUploader(this.$element.querySelector('.js-photogalleryUploader'));
    this.photogalleryUploader.on('upload', image => {
        this.update();
    });

    // в sessionStorage хранится только id фотографии, т.к. большой объём данных может вызывать ошибки
    const photos = JSON.parse(getStorage('photos', '[]'));
    if (photos.length) {
        fetch(`ajax_fetch_unattached_photos/?ids=${photos.join(',')}`, {
            method: 'GET',
            headers: {
                'X-Requested-With': 'XMLHttpRequest',
            },
        })
        .then(response => response.json())
        .then(r => {
            (r.data || []).forEach(image => {
                this.photogalleryUploader.addImage(image.id, image.url);
            });
        })
        .finally(() => {
            this.update();
        });
    }

    ['uploaded', 'removed'].forEach(event => {
        this.photogalleryUploader.on(event, image => {
            setStorage('photos', JSON.stringify(this.photogalleryUploader.images.map(image => image.id)));
            this.update();
        });
    });

    // загрузка из сессии
    this.sessionLoad();

    this.applyValidator(this.$form.elements['title'], ['input'], $element => $element.value.trim().length > 1);
    this.applyValidator(this.$form.elements['content'], ['input'], $element => $element.value.replace(/\s/g, '').length > 1);

    autoUpdateTextareaHeight(this.$form.elements['content']);

    this.update();
}

ReviewCreate.prototype = Object.create(ComponentElement.prototype);
Object.defineProperty(ReviewCreate.prototype, 'constructor', {
    value: ReviewCreate,
    enumerable: false,
    writable: true,
});

ReviewCreate.prototype.sessionLoad = function() {
    this.$form.elements['title'].value = getStorage('title', '');
    this.$form.elements['content'].value = getStorage('content', '');
}

ReviewCreate.prototype.sessionSave = function() {
    setStorage('title', this.$form.elements['title'].value);
    setStorage('content', this.$form.elements['content'].value);
}

ReviewCreate.prototype.sessionRemove = function() {
    removeStorage('stars');
    removeStorage('title');
    removeStorage('content');
    removeStorage('photos');
}

ReviewCreate.prototype.reset = function() {
    ['title', 'content'].forEach(name => {
        this.$form.elements[name].value = '';
        this.$form.elements[name].classList.remove('is-error');
        this.$form.elements[name].classList.remove('is-valid');
    });

    this.starsInput.reset();
    this.photogalleryUploader.reset();
    this.$error.textContent = '';

    this.sessionRemove();
    this.update();
}

/**
 * Открытие / закрытие элемента
 */
ReviewCreate.prototype.toggle = function() {
    if (this.isOpened()) {
        this.close();
    } else {
        this.open();
    }
}

/**
 * Открытие элемента
 */
ReviewCreate.prototype.isOpened = function() {
    return this.$element.classList.contains('is-active');
}

/**
 * Открытие элемента
 */
ReviewCreate.prototype.open = function() {
    this.$element.classList.add('is-active');
    this.$open.textContent = 'Отменить';

    setTimeout(() => {
        Scroll.to(this.$form, {
            interruptable: false,
            time: 0.5,
            offsetY: window.innerHeight / 2 - this.$form.offsetHeight / 2,
        }).finally(() => {
            this.$form.elements['title'].focus();
        });
    }, 200);
}

/**
 * Закрытие элемента
 */
ReviewCreate.prototype.close = function() {
    this.$element.classList.remove('is-active');
    this.$open.textContent = 'Написать отзыв';
}

/**
 * Установка функции валидатора на элемент ввода
 * @param {Element}  $element  Поле ввода
 * @param {Array}    events    Список событий (change, input, ...)
 * @param {Function} validator Функция - валидатор, должна возвращать true если ввод корректный
 */
ReviewCreate.prototype.applyValidator = function($element, events, validator) {
    events.forEach(event => {
        $element.addEventListener(event, e => {
            const valid = validator($element);
            $element.classList.toggle('is-valid', valid);
            $element.classList.toggle('is-error', !valid);
        });
    });

    $element.classList.toggle('is-valid', validator($element));
}

ReviewCreate.prototype.validate = function() {
    return [
        () => !!this.starsInput.getValue(),
        () => !this.photogalleryUploader.hasUploadingImages(),
        () => this.$form.elements['title'].classList.contains('is-valid'),
        () => this.$form.elements['content'].classList.contains('is-valid'),
    ].every(cb => cb());
}

ReviewCreate.prototype.update = function() {
    this.$submit.toggleAttribute('disabled', !this.validate());
}

/**
 * Событие ввода в инпуты
 * @param {Event} e DOM событие
 */
ReviewCreate.prototype.onFormChangeEvent = function(e) {
    this.sessionSave();
    this.update();
}

/**
 * Событие ввода в инпуты
 * @param {Event} e DOM событие
 */
ReviewCreate.prototype.onFormInputEvent = function(e) {
    this.update();
}

/**
 * Отправка формы
 * @param  {FormData} formData Объект формы
 * @return {Promise}
 */
ReviewCreate.prototype.submit = function(formData) {
    return fetch('ajax_create_review/', {
        method: 'POST',
        headers: {
            'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
    })
    .then(response => response.json())
    .then(r => {
        if (r.status != 'ok') {
            throw r;
        }

        return r.data;
    });
}

ReviewCreate.prototype.onFormSubmitEvent = function(e) {
    e.preventDefault();
    e.stopPropagation();

    // от повторных нажатий
    this.$submit.toggleAttribute('disabled', true);
    this.$submit.classList.add('is-loading');
    this.$error.textContent = '';

    const formData = new FormData(this.$form);

    this.submit(formData).then(review => {
        this.reset();
        Review.fetch(review.id).then(content => {
            const $review = createElement(content);
            this.$reviewsListContainer.appendChild($review);
            new Review($review, this.reviews);
            Scroll.to($review, {time: 0.3, offsetY: 80}).finally(() => {
                this.close();
            });
        });
    }).catch(r => {
        (r.fields || []).forEach(field => {
            if (this.$form.elements[field]) {
                this.$form.elements[field].classList.remove('is-valid');
                this.$form.elements[field].classList.add('is-error');
            }
        });

        if (r.message) {
            this.$error.textContent = r.message;
        }
    }).finally(() => {
        this.$submit.toggleAttribute('disabled', false);
        this.$submit.classList.remove('is-loading');
    });
}

export default ReviewCreate;
